import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, webPageOf } from "@/lib/marketing/ppl-schema";
import { useMNavigate } from "@/lib/marketing/site-version";
import { useRef, useState } from "react";
import { Reveal } from "@/components/marketing/reveal";
import { Button } from "@/components/ui/button";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { CaseStudyGate, useCaseStudyAccess } from "@/components/marketing/proof/case-study-gate";
import { ArrowRight, Clock, DollarSign, PhoneCall } from "@/lib/public-icons";
import { EmployeeAvatarByName } from "@/components/marketing/site/employee-avatar-by-name";
import { WORKFORCE_TONES_ON_DARK as WORKFORCE_TONES } from "@/lib/marketing/tones";
import { WeekBars } from "@/components/marketing/site/proof-section-week-bars";
import { BudgetBars } from "@/components/marketing/site/proof-section-budget-bars";

/* Proof index and lead capture: each card is the result itself, with three figures and a picture of
 * each, repeated verbatim from the linked study and never rounded up. The email buys the working:
 * method, definitions, sensitivity tables, appendix. */

/** Sarah's color and Nancy's, the pair each carries everywhere on this site. */
const TONE = {
  sarah: WORKFORCE_TONES.sarah,
  nancy: WORKFORCE_TONES.nancy,
};

/** Calls per week over the study's eight full weeks. */
const SARAH_WEEKS = [173, 368, 360, 505, 463, 444, 311, 204];
/** Cost per resident per day by month, against the $9.77 the same kitchens ran at before Nancy. */
const NANCY_MONTHS = [
  { m: "Jun", v: 5.29 },
  { m: "Jul", v: 7.23 },
  { m: "Aug", v: 6.2 },
];

const STUDIES = [
  {
    slug: "sarah-front-desk",
    href: "/case-studies/sarah-front-desk",
    agent: "Sarah",
    role: "AI Front Desk Receptionist",
    tone: TONE.sarah,
    title: "Every call answered, at every hour",
    hook: "Two small communities, eight weeks, one telephone line each. Nobody was hired, and no call went to voicemail.",
    kpis: [
      { icon: PhoneCall, value: "2,856", label: "calls answered in 56 days" },
      { icon: Clock, value: "39 h", label: "of reception a month, handled" },
      {
        icon: DollarSign,
        value: "$4,700",
        label: "saved a month vs a receptionist at $2 an active minute",
      },
    ],
    footnote:
      "388 of those calls arrived after the desk had closed. 224 messages were written up and emailed.",
    asOf: "Records read 7 September 2026",
    chart: "weeks" as const,
  },
  {
    slug: "nancy-purchasing",
    href: "/case-studies/nancy-purchasing",
    agent: "Nancy",
    role: "AI Procurement Manager",
    tone: TONE.nancy,
    title: "About 30% off the food bill, two ways",
    hook: "Three months of buying for two kitchens: a list built from the menu, then the best price across three stores, measured from the vendors' own records.",
    kpis: [
      { icon: DollarSign, value: "$6.63", label: "per resident per day, raw food" },
      {
        icon: Clock,
        value: "~30%",
        label: "less than before Nancy: half from the menu, half from price",
      },
      { icon: DollarSign, value: "$4,600", label: "saved a month, for 48 residents" },
    ],
    footnote:
      "Roughly half from buying to the menu, less waste, no over-delivery, and half from price matching across three vendors. 251 orders, 88 days, every month below the $9.77 the same kitchens ran at before Nancy.",
    asOf: "Records read 5 and 8 September 2026",
    chart: "budget" as const,
  },
];

function MarketingCaseStudies() {
  const { unlocked, unlock } = useCaseStudyAccess();
  const navigate = useMNavigate();
  /** Where the reader was heading when they hit the gate, so the form can send
   *  them straight there rather than back to a list they have already read. */
  const wanted = useRef<string | null>(null);
  const [gateOpen, setGateOpen] = useState(false);
  const gateRef = useRef<HTMLDivElement | null>(null);

  const open = (href: string) => {
    if (unlocked) {
      navigate(href);
      return;
    }
    wanted.current = href;
    setGateOpen(true);
    /* One frame, so the form exists before it is scrolled to. */
    window.requestAnimationFrame(() => {
      gateRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    });
  };

  return (
    <MarketingShell>
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="relative mx-auto max-w-4xl px-4 pt-14 pb-10 md:pt-20 md:pb-14">
          <Reveal>
            <p className="text-sm font-medium text-muted-foreground">Proof of work</p>
            <h1
              className="font-display mt-3 text-balance text-4xl font-medium leading-[1.02] tracking-[-0.035em] md:text-6xl"
              data-testid="text-case-studies-title"
            >
              What they actually did
            </h1>
            <p className="mt-5 max-w-2xl text-pretty text-lg text-muted-foreground">
              Two AI employees, two communities in Texas, measured from the communities' own
              records. The numbers below are the results. The reports behind them show the working.
            </p>
          </Reveal>
        </div>
      </section>

      <section>
        <div className="mx-auto grid max-w-6xl gap-5 px-4 py-12 md:py-16 lg:grid-cols-2">
          {STUDIES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 80}>
              <article
                className="flex h-full flex-col rounded-2xl border border-border/60 bg-background p-6 md:p-8"
                style={{ background: `linear-gradient(160deg, ${s.tone[0]}12, transparent 55%)` }}
                data-testid={`card-case-study-${s.agent.toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <EmployeeAvatarByName
                    name={s.agent}
                    size={40}
                    testId={`orb-${s.agent.toLowerCase()}`}
                  />
                  <div className="min-w-0">
                    <p
                      className="text-sm font-medium text-muted-foreground"
                      style={{ color: s.tone[0] }}
                    >
                      {s.agent}
                    </p>
                    <p className="text-xs text-muted-foreground">{s.role}</p>
                  </div>
                </div>

                <h2 className="font-display mt-5 text-2xl font-semibold tracking-tight md:text-3xl">
                  {s.title}
                </h2>
                <p className="mt-3 text-muted-foreground">{s.hook}</p>

                <dl className="mt-6 grid grid-cols-3 gap-3 border-y border-border/60 py-5">
                  {s.kpis.map((k) => (
                    <div key={k.label}>
                      <k.icon className="size-4" style={{ color: s.tone[0] }} aria-hidden="true" />
                      <dt className="font-display mt-2 text-2xl font-semibold tracking-tight tabular-nums md:text-3xl">
                        {k.value}
                      </dt>
                      <dd className="mt-1 text-xs leading-snug text-muted-foreground">{k.label}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-5">
                  {s.chart === "weeks" ? (
                    <WeekBars data={SARAH_WEEKS} tone={s.tone} />
                  ) : (
                    <BudgetBars data={NANCY_MONTHS} tone={s.tone} />
                  )}
                </div>

                <p className="mt-4 text-sm text-muted-foreground">{s.footnote}</p>

                <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-7">
                  <span className="text-xs text-muted-foreground">{s.asOf}</span>
                  <Button
                    onClick={() => open(s.href)}
                    className="font-ui"
                    data-testid={`button-open-${s.slug}`}
                  >
                    {unlocked ? "Read the study" : "Read the full study"}
                    <ArrowRight className="ml-2 size-4" aria-hidden="true" />
                  </Button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* The gate, once a reader has asked for a report. It is not in the way
          until then: the results above are free to read and to quote. */}
      {!unlocked && gateOpen && (
        <section className="border-t border-border/60 bg-card/40">
          <div ref={gateRef} className="mx-auto max-w-2xl px-4 py-12 md:py-16">
            <CaseStudyGate
              study="Both case studies"
              slug="index"
              promise="Both reports open: the method, every definition, the sensitivity tables and the monthly appendices. One address, and they stay open on this browser."
              onUnlocked={() => {
                unlock();
                const href = wanted.current;
                if (href) window.setTimeout(() => navigate(href), 150);
              }}
            />
          </div>
        </section>
      )}

      <section className="border-t border-border/60">
        <div className="mx-auto max-w-4xl px-4 py-12 md:py-14">
          <Reveal>
            <div className="grid gap-6 text-foreground/80 md:grid-cols-3">
              <div>
                <p className="font-semibold text-foreground">Measured means measured</p>
                <p className="mt-1.5 text-sm leading-relaxed">
                  Every figure comes from complete production records, not a sample, with the
                  definitions written out in the method section.
                </p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Estimates say what they assume</p>
                <p className="mt-1.5 text-sm leading-relaxed">
                  Where a dollar figure rests on an assumption, the assumption is named, sourced,
                  and shown under other values.
                </p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Two communities, in production</p>
                <p className="mt-1.5 text-sm leading-relaxed">
                  Pine Lodge and Reunion Inn, Texas: their own phone lines, retailer accounts and
                  resident record, read on the dates shown.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingCta
        title="See both agents at work in your communities"
        subtitle="Book a demo, or run the live demos on this site first."
        secondaryLabel="Try it live"
        secondaryHref="/experience"
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/case-studies/")({
  component: MarketingCaseStudies,
  head: () => {
    const seo = {
      title: "Case studies | PEOPLE AI Workforce",
      description:
        "What two AI staff members did in production at two assisted-living communities in Texas, measured from the communities' own records: the front desk and the kitchen purchasing.",
      path: "/v2/case-studies",
    };
    return marketingHead({
      ...seo,
      schema: [webPageOf(seo), breadcrumbs({ name: "Case studies", path: "/case-studies" })],
    });
  },
});
